import React, { useEffect } from 'react';
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom';
import { Mail, ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { blogPosts } from '../data/blogData';
import FooterTrust from '../components/FooterTrust';
import NotFound from '../components/NotFound';

const BOT_LINK = "https://t.me/instant_email_bot";

// Old sitemap URLs that never matched a real article
const LEGACY_SLUGS = {
  "best-telegram-email-bot-2026": "/blog/best-telegram-email-bots-2026",
  "temporary-email-telegram": "/temporary-email-telegram",
  "email-to-telegram-group": "/receive-email-on-telegram",
  "telegram-email-bot-vs-gmail": "/gmail-in-telegram",
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts.find(p => p.slug === slug);
  const postIndex = blogPosts.findIndex(p => p.slug === slug);
  const nextPost = blogPosts[postIndex + 1];
  const prevPost = blogPosts[postIndex - 1];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (post) {
      document.title = `${post.title} | TelegramEmailBot`;
      const updateMeta = (name, content, attr = 'name') => {
        let el = document.querySelector(`meta[${attr}="${name}"]`);
        if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
        el.setAttribute('content', content);
      };
      updateMeta('description', post.excerpt || post.title);
      updateMeta('og:title', `${post.title} | TelegramEmailBot`, 'property');
      updateMeta('og:description', post.excerpt || post.title, 'property');
      updateMeta('og:url', `https://telegramemailbot.com/blog/${post.slug}`, 'property');
      const link = document.querySelector('link[rel="canonical"]') || (() => { const l = document.createElement('link'); l.rel = 'canonical'; document.head.appendChild(l); return l; })();
      link.href = `https://telegramemailbot.com/blog/${post.slug}`;
    }
  }, [slug, post]);

  if (!post) {
    if (LEGACY_SLUGS[slug]) return <Navigate to={LEGACY_SLUGS[slug]} replace />;
    return <NotFound label="Article Not Found" backTo="/blog" backLabel="Back to Blog" />;
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="post-logo">
            <Mail className="w-6 h-6 text-[#24A1DE]" />
            <span className="font-bold text-lg text-gray-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Instant Email Bot</span>
          </Link>
          <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold bg-[#24A1DE] text-white px-5 py-2 rounded-md hover:bg-[#1d8abf] transition-all" data-testid="post-header-cta">
            Open Bot
          </a>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 py-12 sm:py-16" data-testid="blog-post-content">
        <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 transition-colors mb-8" data-testid="post-back-blog">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        <div className="flex items-center gap-3 text-xs text-gray-400 mb-4">
          <span>{post.date}</span>
          <span>&middot;</span>
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.readTime}</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="post-title">{post.title}</h1>
        <p className="text-base text-gray-500 leading-relaxed mb-8">{post.description}</p>

        <div className="prose prose-gray max-w-none
          [&_h2]:font-bold [&_h2]:text-xl [&_h2]:text-gray-900 [&_h2]:mt-10 [&_h2]:mb-4
          [&_h3]:font-bold [&_h3]:text-lg [&_h3]:text-gray-900 [&_h3]:mt-8 [&_h3]:mb-3
          [&_p]:text-sm [&_p]:text-gray-600 [&_p]:leading-relaxed [&_p]:mb-4
          [&_ul]:text-sm [&_ul]:text-gray-600 [&_ul]:mb-4 [&_ul]:space-y-2 [&_ul]:pl-4 [&_ul]:list-disc
          [&_ol]:text-sm [&_ol]:text-gray-600 [&_ol]:mb-4 [&_ol]:space-y-2 [&_ol]:pl-4 [&_ol]:list-decimal
          [&_a]:text-[#24A1DE] [&_a]:font-semibold [&_a]:hover:underline
          [&_code]:bg-gray-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:text-gray-700
          [&_table]:w-full [&_table]:text-sm [&_table]:border-collapse [&_table]:mb-6
          [&_th]:text-left [&_th]:font-bold [&_th]:text-gray-900 [&_th]:py-2 [&_th]:px-3 [&_th]:border-b-2 [&_th]:border-gray-200
          [&_td]:py-2 [&_td]:px-3 [&_td]:border-b [&_td]:border-gray-100 [&_td]:text-gray-600
          [&_strong]:text-gray-900
        "
          style={{ fontFamily: "'Manrope', sans-serif" }}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* CTA inside article */}
        <div className="bg-[#24A1DE]/10 border border-[#24A1DE]/20 rounded-lg p-6 my-10 text-center" data-testid="post-inline-cta">
          <h3 className="font-bold text-lg text-gray-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Get Your Free Email in Telegram</h3>
          <p className="text-sm text-gray-500 mb-4">No signup. No credit card. Instant email with AI summaries.</p>
          <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#24A1DE] text-white font-semibold px-6 py-3 rounded-md hover:bg-[#1d8abf] transition-all">
            Open Telegram Bot <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Navigation between posts */}
        <div className="flex items-center justify-between mt-12 pt-8 border-t border-gray-200">
          {prevPost ? (
            <Link to={`/blog/${prevPost.slug}`} className="text-sm text-gray-500 hover:text-gray-900 transition-colors" data-testid="post-prev">
              <ArrowLeft className="w-4 h-4 inline mr-1" /> Previous
            </Link>
          ) : <div />}
          {nextPost ? (
            <Link to={`/blog/${nextPost.slug}`} className="text-sm text-gray-500 hover:text-gray-900 transition-colors" data-testid="post-next">
              Next <ArrowRight className="w-4 h-4 inline ml-1" />
            </Link>
          ) : <div />}
        </div>
      </article>

      <section className="bg-[#24A1DE] py-12 px-4 text-center" data-testid="post-bottom-cta">
        <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Ready to Try It?</h2>
        <p className="text-white/80 text-sm mb-6">Free for 1 month. No credit card needed.</p>
        <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-[#24A1DE] font-bold px-7 py-3 rounded-md hover:bg-gray-100 transition-all" data-testid="post-bottom-cta-button">
          Get Started ($4.99) <ArrowRight className="w-4 h-4" />
        </a>
      </section>

      <footer className="bg-gray-900 text-gray-300 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <span>&copy; 2026 TelegramEmailBot. All rights reserved.</span>
            <div className="flex flex-wrap gap-4">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            </div>
          </div>
          <FooterTrust variant="dark" />
        </div>
      </footer>
    </div>
  );
}
